import {
  ArrowLeft,
  LucideCalendar,
  LucideCoins,
  LucideFileText,
  LucideFootprints,
  LucideHeart,
  LucidePill,
  LucideShoppingBag,
  LucideSmartphone,
  LucideStethoscope,
} from "lucide-react";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "src/components/app/layout/Layout";
import Loader from "src/components/common/Loader";
import Shimmer from "src/components/common/Shimmer";
import { APP_LINKS, POST_MESSAGE_EVENTS } from "src/constants/common";
import { DATE_FORMAT_STRING } from "src/constants/dataTimeFormats";
import { sendMessageToParent } from "src/helpers/common";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as walletActions from "src/redux/slices/walletSlice";
import { records, wallet } from "src/types/index";

const WalletScreen: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const supportProgramKey = params["support-program-key"];
  const { walletAmount, coinsValue, walletStatement } = useAppSelector(
    (state) => state.wallet
  );
  const { patient } = useAppSelector((state) => state.auth);
  const [coins, setCoins] = useState<number>(0);
  const [pageNo, setPageNo] = useState<number>(0);

  const fetchWalletAmount = () => {
    const payload = {
      contact_no: patient?.data?.contact_no,
    };
    new Promise((resolve, reject) => {
      dispatch(
        walletActions.fetchWalletAmountRequest({ payload, resolve, reject })
      );
    })
      .then((res: any) => {
        let coins = Number(res?.data?.balance ?? 0) / 100;
        if (coinsValue) {
          coins = coins * coinsValue;
        }
        setCoins(coins);
      })
      .catch((error) => {
        console.error("error", error);
      });
  };

  useEffect(() => {
    fetchWalletAmount();
  }, []);

  const fetchTransactionHistory = () => {
    const payload = {
      page_no: pageNo,
      count: 10,
    };
    new Promise((resolve, reject) => {
      dispatch(
        walletActions.fetchWalletStatementRequest({ payload, resolve, reject })
      );
    });
  };

  useEffect(() => {
    fetchTransactionHistory();
  }, []);

  const loadMoreTransactionHistory = () => {
    console.log("pageNo =>", pageNo);
    if (!walletStatement?.isLastPageReached) {
      const payload = {
        patient_id: patient?.data?.patient_id,
        page_no: pageNo + 1,
        count: walletStatement?.count,
        filter: "",
      };
      new Promise((resolve, reject) => {
        dispatch(
          walletActions.fetchWalletStatementRequest({
            payload: payload,
            resolve,
            reject,
          })
        );
      });
      setPageNo(pageNo + 1);
    }
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-lg p-4 text-white bg-gradient-to-r from-[var(--app-primary)] to-[var(--app-primary-hover)] z-10">
        <div className="flex items-center gap-2">
          <button
            className="flex items-center rounded-full hover:bg-gray-200 w-6 h-6"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h5 className="text-base font-semibold line-clamp-1 flex-1">
            Your Wallet
          </h5>
        </div>
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-sm">
            <LucideCoins className="w-5 h-5" />
            <p>Available Balance</p>
          </div>
          {walletAmount?.isLoading ? (
            <div className="relative w-full">
              <Shimmer width="150px" height="30px" />
            </div>
          ) : (
            <div className="relative w-full">
              <h2 className="text-3xl font-bold">{coins} Coins</h2>
              <p className="text-sm">= ₹{coins}</p>
            </div>
          )}
        </div>
        <div className="absolute inset-0 pointer-events-none -z-10 opacity-50">
          <div className="absolute top-2 right-4 w-2.5 h-2.5 rounded-full bg-[var(--app-primary)]"></div>
          <div className="absolute top-8 right-12 w-4 h-4 rounded-full bg-[var(--app-primary)]"></div>
          <div className="absolute top-1/2 left-1/3 w-6 h-6 rounded-full bg-[var(--app-primary)]"></div>
          <div className="absolute top-1/3 right-6 w-5 h-5 rounded-full bg-[var(--app-primary)]"></div>
          <div className="absolute top-2/3 left-6 w-2 h-2 rounded-full bg-[var(--app-primary)]"></div>
          <div className="absolute bottom-8 left-8 w-4 h-4 rounded-full bg-[var(--app-primary)]"></div>
          <div className="absolute bottom-4 right-4 w-2.5 h-2.5 rounded-full bg-[var(--app-primary)]"></div>
          <div className="absolute bottom-6 left-1/4 w-2 h-2 rounded-full bg-[var(--app-primary)]"></div>
        </div>
      </div>
      <div className="py-4 mx-4 flex flex-col gap-4 mt-40">
        <div className="relative w-full flex flex-col gap-3">
          <h4 className="text-base font-semibold text-black">
            Put Your Coins to Use
          </h4>
          <CoinToUseCard />
        </div>
        <div className="relative w-full flex flex-col gap-3">
          <h4 className="text-base font-semibold text-black">
            Transaction History
          </h4>
          <div className="w-full flex flex-col gap-2">
            {walletStatement?.items?.length === 0 &&
              walletStatement?.isLoading && (
                <>
                  <Shimmer width="100%" height="60px" />
                  <Shimmer width="100%" height="60px" />
                </>
              )}
            {/* TODO: Infinite scroll */}
            {walletStatement?.items?.length ? (
              <InfiniteScroll
                dataLength={walletStatement?.items?.length}
                next={loadMoreTransactionHistory}
                hasMore={!walletStatement?.isLastPageReached}
                loader={
                  walletStatement?.isLoading && (
                    <div className="flex items-center justify-center py-4">
                      <Loader size="sm" />
                    </div>
                  )
                }
                scrollableTarget="scrollableTarget"
              >
                <div className="space-y-2">
                  {walletStatement?.items?.map((item, index) => (
                    <TransactionHistoryCard key={index} item={item} />
                  ))}
                </div>
              </InfiniteScroll>
            ) : (
              !walletStatement?.isLoading && (
                <div className="text-center py-10">
                  <span className="text-sm text-gray-500">
                    No history found
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

const CoinToUseCard = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const screenName = params["screen-name"];
  const navigate = useNavigate();
  const coinUseData = [
    {
      id: 1,
      title: "Book Appointment",
      description: "With our Nutritionists",
      icon: LucideStethoscope,
      onClick: () => {
        navigate(`/app/${supportProgramKey}/book-consultation`, {
          state: {
            screenName: screenName,
          },
        });
      },
    },
    {
      id: 2,
      title: "Book Lab Test",
      description: "At discounted prices",
      icon: LucideStethoscope,
      onClick: () => {
        navigate(`/app/${supportProgramKey}/book-lab-test`);
      },
    },
    {
      id: 3,
      title: "Health Store",
      description: "Buy essentials health products",
      icon: LucideHeart,
      onClick: () =>
        // sendMessageToParent(POST_MESSAGE_EVENTS.OPEN_EXTERNAL_LINK, {
        //   link: APP_LINKS.HEALTH_STORE_LINK,
        // }),
        sendMessageToParent(POST_MESSAGE_EVENTS.NAVIGATE_TO_WEBVIEW, {
          url: APP_LINKS.HEALTH_STORE_LINK,
          title: "Health Store",
        }),
    },
  ];
  return (
    <div className="flex items-start gap-4 overflow-x-auto">
      {coinUseData.map((item) => (
        <div
          key={item.id}
          className="w-full min-w-[160px] min-h-[160px] relative bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-center"
          onClick={item?.onClick}
        >
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <div className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-full text-[var(--app-primary)]">
              <item.icon className="w-5 h-5" />
            </div>
            <div className="w-full space-y-1">
              <h6 className="text-sm font-semibold">{item.title}</h6>
              <p className="text-xs text-gray-500">{item.description}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TransactionHistoryCard = ({
  item,
}: {
  item: wallet.WalletStatementItemType;
}) => {
  const { coinsValue } = useAppSelector((state) => state.wallet);

  const checkIsCredit = item?.credit !== "0" ? true : false;
  const amount = checkIsCredit ? Number(item?.credit) : Number(item?.debit);
  const applyCreditTextColor = checkIsCredit
    ? "text-green-500"
    : "text-red-500";

  const coins = useMemo(() => {
    let coins = Number(amount ?? 0) / 100;
    if (coinsValue) {
      coins = coins * coinsValue;
    }
    return coins;
  }, [amount, coinsValue]);

  const renderIcon = (type: string | undefined) => {
    switch (type) {
      case "lab_test_booking":
      case "bca_criteria":
        return LucideStethoscope;
      case "Care Product":
        return LucideShoppingBag;
      case "plan_booking":
        return LucideHeart;
      case "visit_status_approved":
        return LucideFileText;
      case "step_count":
        return LucideFootprints;
      default:
        return LucideCoins;
    }
  };

  const renderTitle = (title: string) => {
    switch (title) {
      case "lab_test_booking":
        return "Lab Test";
      case "plan_booking":
        return "Consultation";
      case "visit_status_approved":
        return "Document Approved";
      case "bca_criteria":
        return "Smart scale synced";
      case "step_count":
        return "Steps completed";
      default:
        return title;
    }
  };
  return (
    <div className="w-full relative bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 border border-gray-200 rounded-full text-[var(--app-primary)]">
            {(() => {
              const Icon = renderIcon(item?.notes?.type);
              return Icon ? <Icon className="w-4 h-4" /> : null;
            })()}
          </div>
          <div className="flex flex-col gap-1">
            <h6 className="text-sm font-medium line-clamp-1">
              {renderTitle(item?.notes?.type ?? "-")}
            </h6>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <LucideCalendar className="w-3 h-3" />
              <p>
                {moment
                  .unix(Number(item?.created_at))
                  .format(DATE_FORMAT_STRING)}
              </p>
            </div>
          </div>
        </div>
        <div className="w-10 min-w-10 flex flex-col items-end">
          <h6 className={`text-sm font-semibold ${applyCreditTextColor}`}>
            {checkIsCredit ? "+" : "-"}
            {coins}
          </h6>
          <p className="text-xs text-gray-400">coins</p>
        </div>
      </div>
    </div>
  );
};

export default WalletScreen;
